// Maquinaria de ordenacao dos getters de tarefas.
//
// Modulo PRIVADO — nao e reexportado por `getters.js`, senao `applySort` e
// `sortByOrder` virariam getters Vuex. So `sortComparator` e publico, e quem
// o reexporta e `getters-projection.js` (era de la que ele saia antes do
// split que atendeu o S104).
import { parseTaskDate } from '@modules/tarefas/utils/isOverdue'
import { taskStatusId } from '@modules/tarefas/utils/optimisticStatusMove'
import { TASK_SORT_FIELDS, TASK_PRIORITY_RANK } from '@modules/tarefas/constants/Text'

// Lookup: field key -> sort type ('date' | 'priority' | 'status' | 'string').
// Built from the single source of truth in constants/Text.js so the comparator
// and the UI control never drift.
const _SORT_TYPE_BY_FIELD = {}
for (const def of TASK_SORT_FIELDS) {
  if (def.field != null) _SORT_TYPE_BY_FIELD[def.field] = def.type
}

function sortByOrder(arr) {
  return arr.slice().sort(function (a, b) {
    const orderA = a.sortOrder == null ? Number.MAX_SAFE_INTEGER : a.sortOrder
    const orderB = b.sortOrder == null ? Number.MAX_SAFE_INTEGER : b.sortOrder
    return orderA - orderB
  })
}

// --- Field sort comparator ---------------------------------------------
// A single typed comparator drives both Kanban and List ordering when the
// user picks a sort field. Null/empty values ALWAYS sort last regardless of
// direction (AC-S7). Only parent/root tasks are passed here — subtasks live
// nested in `task.subtarefas` and are never re-sorted (AC-S6).

// Reads a display name from an assignee-like object, tolerant of the `nome`
// (camelized PT) and `name` (legacy) shapes. Returns null when absent.
function _displayName(obj) {
  if (!obj) return null
  return obj.nome || obj.name || null
}

// Reads the responsável display name from the task, tolerant of multiple
// backend shapes (responsavelPrincipal, then first assignee, then legacy
// single `assignee`).
function _responsavelName(task) {
  if (!task) return null
  const assignees = Array.isArray(task.assignees) ? task.assignees : []
  return (
    _displayName(task.responsavelPrincipal) ||
    _displayName(assignees[0]) ||
    _displayName(task.assignee)
  )
}

// Reads the creation timestamp. The backend has NO `createdAt` field — the
// creation timestamp is `dataRegistro` (camelized `data_registro`). The UI
// keeps the `createdAt` key/label but the comparator maps it here.
function _createdAtValue(task) {
  if (!task) return null
  if (task.dataRegistro != null) return task.dataRegistro
  if (task.data_registro != null) return task.data_registro
  return null
}

// Reads the linked client display name. The backend may ship the client as a
// full object (`task.cliente`) OR as a bare id (`clienteAppId` /
// `id_cliente_app`). When only an id is present we resolve the name from the
// assignees-clients cache via `ctx.clientNameById` (built in `tasksByStatus`),
// mirroring how `task-item.vue` hydrates the client name for display.
function _clienteName(task, ctx) {
  if (!task) return null
  const obj = task.cliente
  if (obj && (obj.nome || obj.name)) return obj.nome || obj.name
  const map = ctx && ctx.clientNameById
  if (map) {
    let id = task.clienteAppId
    if (id == null) id = task.id_cliente_app
    if (id != null && map[id]) return map[id]
  }
  return null
}

// Extracts the raw comparable value for a given field, normalizing the
// per-field aliases the backend may emit (camelCase vs snake_case). `ctx`
// carries cross-task lookups (e.g. the client name map) for fields whose
// display value is not stored directly on the task.
function _rawSortValue(task, field, ctx) {
  if (!task) return null
  if (field === 'dueDate') return task.dueDate == null ? task.due_date : task.dueDate
  if (field === 'startDate') return task.startDate == null ? task.start_date : task.startDate
  if (field === 'createdAt') return _createdAtValue(task)
  if (field === 'priority') return task.priority
  if (field === 'status') return taskStatusId(task)
  if (field === 'title') return task.title
  if (field === 'responsavel') return _responsavelName(task)
  if (field === 'cliente') return _clienteName(task, ctx)
  return task[field]
}

// Returns a numeric/string comparable for the given field+type, or `null`
// when the value is missing/empty/unparseable (so it sorts last).
function _comparable(task, field, type, ctx) {
  const raw = _rawSortValue(task, field, ctx)
  if (raw == null || raw === '') return null
  if (type === 'date') {
    const parsed = parseTaskDate(raw)
    return parsed ? parsed.valueOf() : null
  }
  if (type === 'priority') {
    const rank = TASK_PRIORITY_RANK[raw]
    return rank == null ? null : rank
  }
  if (type === 'status') {
    // Sort rank = the status `position` within the active folder (R5).
    // `raw` is the task's statusId; resolve its position from the status map
    // carried in `ctx`. Unknown/missing status → null (sorts last).
    const map = ctx && ctx.statusMap
    const status = map && raw != null ? map[raw] : null
    if (!status || status.position == null) return null
    return status.position
  }
  // string (title / responsavel): normalize for locale-aware compare below.
  return String(raw)
}

// PURE per-field comparison: returns a negative/positive/zero number for a
// single field. Null-last in BOTH directions (missing values always sort
// last). Returns 0 on equal NON-null values AND on both-null — this is what
// lets the cascade fall through to the next field (and finally to sortOrder).
function fieldCompare(a, b, field, type, direction, ctx) {
  const dir = direction === 'desc' ? -1 : 1
  const va = _comparable(a, field, type, ctx)
  const vb = _comparable(b, field, type, ctx)
  // Null-last regardless of direction; both-null is a tie (0) so the cascade
  // can promote the next field.
  if (va == null && vb == null) return 0
  if (va == null) return 1
  if (vb == null) return -1
  let cmp
  if (typeof va === 'string') {
    cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' })
  } else if (va < vb) {
    cmp = -1
  } else if (va > vb) {
    cmp = 1
  } else {
    cmp = 0
  }
  return cmp * dir
}

// Single-field comparator for `arr.sort`. Null-last in BOTH directions; ties
// keep the existing relative order via the sortOrder fallback (stable-ish).
// Composed from `fieldCompare` + the manual tie-break so single-field sort
// behavior is byte-for-byte identical to before (still exported/used).
function sortComparator(field, type, direction) {
  return function (a, b) {
    const cmp = fieldCompare(a, b, field, type, direction)
    if (cmp !== 0) return cmp
    // Stable tie-break by manual order (not direction-dependent).
    return sortByOrderValue(a) - sortByOrderValue(b)
  }
}

function sortByOrderValue(task) {
  return task && task.sortOrder != null ? task.sortOrder : Number.MAX_SAFE_INTEGER
}

// Normalizes a raw sortChain into `[{ field, type, direction }]`, dropping
// entries with an unknown/null field or an unmapped type.
function _resolveChain(sortChain) {
  const raw = Array.isArray(sortChain) ? sortChain : []
  const resolved = []
  for (const entry of raw) {
    const field = entry && entry.field
    if (field == null) continue
    const type = _SORT_TYPE_BY_FIELD[field]
    if (!type) continue
    resolved.push({ field: field, type: type, direction: entry && entry.direction })
  }
  return resolved
}

// Applies the multi-column cascade comparator (chain[0]; tie → chain[1]; …;
// final tie → manual sortOrder) or, when the chain is empty/invalid, the
// legacy manual `sortByOrder` (Manual / default) — byte-for-byte identical.
function applySort(arr, sortChain, ctx) {
  const chain = _resolveChain(sortChain)
  if (!chain.length) return sortByOrder(arr)
  return arr.slice().sort(function (a, b) {
    for (const e of chain) {
      const res = fieldCompare(a, b, e.field, e.type, e.direction, ctx)
      if (res !== 0) return res
    }
    return sortByOrderValue(a) - sortByOrderValue(b)
  })
}

// Builds an id -> display-name map from the assignees-clients cache so the
// `cliente` comparator can sort tasks that only carry the client id.
function buildClientNameMap(state) {
  const map = {}
  const clients = (state && state.assignees && state.assignees.clients) || []
  for (const c of clients) {
    if (c && c.id != null) map[c.id] = c.nome || c.name || ''
  }
  return map
}

export { sortByOrder, applySort, sortComparator, buildClientNameMap }
